import { Autocomplete, Button, Dialog, DialogContent, DialogTitle, IconButton, TextField, Typography } from "@mui/material";
import UserTextBox from "./UserTextBox";
import { getTerritories } from "../services/common";
import dayjs from "dayjs";
import React, { SetStateAction } from "react";
import CloseIcon from '@mui/icons-material/Close';
import './AppDetails.css';
import { UserRoleItem, checkIfTerritoryRelated, optionsRoles } from "../services/adminApi";

interface UserRoleDetailsProps {
    isModalOpen: boolean;
    model: UserRoleItem | null;
    handleClose: () => Promise<void>;
    isViewMode: boolean;
    setIsViewMode: React.Dispatch<SetStateAction<boolean>>;
    handleSave: () => Promise<void>;
    handleDelete: () => Promise<void>;
}

const UserRoleDetails = ({ isModalOpen, model, handleClose, isViewMode, setIsViewMode, handleSave, handleDelete }: UserRoleDetailsProps) => {
    const [isValid, setIsValid] = React.useState<boolean>(false);
    const [isEmailValid, setIsEmailValid] = React.useState<boolean>(false);
    const [isTerritoryRequired, setIsTerritoryRequired] = React.useState(false);
    const [modelTerritory, setModelTerritory] = React.useState<string | null>(model?.territory);
    const [modelUserEmail, setModelUserEmail] = React.useState<string | null>(model?.userEmail);
    const [modelUserName, setModelUserName] = React.useState<string | null>(model?.userName);
    const [optionsTerritory, setOptionsTerritory] = React.useState<string[] | null>();
    const slotProps = { inputLabel: { shrink: true } };
    React.useEffect(() => {
        loadTerritories();
    }, []);
    const loadTerritories = async () => {
        const territories = await getTerritories();
        setOptionsTerritory(territories.map(x => x.name));
    };
    const setRole = async (role: string) => {
        model.role = role;
        const isTerReq = checkIfTerritoryRelated(role);
        await setIsTerritoryRequired(isTerReq);
        if (isTerReq === false) {
            await setModelTerritory(null);
        }
        await setIsValid(!!role && isEmailValid && (isTerReq === false || !!modelTerritory))
    };
    React.useEffect(() => {
        if (model != null) {
            model.territory = modelTerritory;
            model.userEmail = modelUserEmail;
            model.userName = modelUserName;
            model.isTerritoryRelated = isTerritoryRequired;
            setIsValid(!!model?.role && isEmailValid && (isTerritoryRequired === false || !!modelTerritory))
        }
    }, [modelTerritory, modelUserEmail, modelUserName, isTerritoryRequired, isEmailValid]);
    React.useEffect(() => {
        setModelTerritory(model?.territory);
        setModelUserEmail(model?.userEmail);
        setModelUserName(model?.userName);
        setIsTerritoryRequired(model?.isTerritoryRelated);
    }, [model, isModalOpen]);
    return (
        <Dialog
            className="details-apps-dialog"
            open={isModalOpen}
            onClose={handleClose}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
        >
            <DialogTitle className="details-apps-header" id="scroll-dialog-title" sx={{ minWidth: '840px' }}>{isViewMode ? "View: " : "Edit: "} {modelUserName}<br /><p className="details-apps-title">{modelUserEmail}</p></DialogTitle>
            <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={(theme) => ({
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: theme.palette.grey[500],
                })}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent dividers={true} sx={{ minWidth: '840px' }}>
                    <Typography className="details-apps-group">User role</Typography>
                    <Typography className="details-apps-title">Manage user role</Typography>
                <div>
                    <UserTextBox canBeEmpty={false} handleValidChange={setIsEmailValid} handleChange={(e) => { setModelUserEmail(e?.email); setModelUserName(e?.name); }} label="User" defaultValue={{ email: modelUserEmail, name: modelUserName }} disabled={isViewMode} />
                    {
                    isViewMode
                            ? <TextField slotProps={slotProps} className="details-apps-text" margin="dense" id="editItem_role" label="Role" defaultValue={model?.role} disabled={true} />
                            : <Autocomplete disabled={false} className="details-apps-autocomplete" value={model?.role} onChange={(e, v) => setRole(v)} options={optionsRoles} renderInput={(params) => <TextField {...params} slotProps={slotProps} className="details-apps-autocomplete-inner" margin="dense" label="Role" />} />
                    }
                </div>
                <div>
                {model?.pseudoId !== null ? 
                        <TextField slotProps={slotProps} className="details-apps-text" margin="dense" id="editItem_lastUpdateTime" label="Last update" defaultValue={(model?.lastUpdateTime == null) ? null : dayjs(model?.lastUpdateTime).format('YYYY-MM-DD HH:mm')} disabled={true} />
                    : ''
                    }
                    {isTerritoryRequired ?
                        isViewMode
                            ? <TextField slotProps={slotProps} className="details-apps-text" margin="dense" id="editItem_territory" label="Territory" defaultValue={modelTerritory} disabled={true} />
                            : <Autocomplete disabled={false} className="details-apps-autocomplete" value={modelTerritory} onChange={(e, v) => setModelTerritory(v)} options={optionsTerritory} renderInput={(params) => <TextField slotProps={slotProps} {...params} className="details-apps-autocomplete-inner" margin="dense" label="Territory" />} />
                        : ''}
                </div>
                    {isViewMode
                    ? <Button style={{ margin: '10px', float: 'left', width: '100px', backgroundColor: '#C52A1A', textTransform: 'none' }} variant="contained" color="success" onClick={handleDelete}>Delete</Button>
                    : ''
                    }
                    {isViewMode
                        ? <Button style={{ margin: '10px', float: 'right', width: '100px', backgroundColor: '#FD5107', textTransform: 'none' }} variant="contained" color="success" onClick={() => setIsViewMode(false)}>Edit</Button>
                        : <Button style={{ margin: '10px', float: 'right', width: '100px', backgroundColor: '#FD5107', textTransform: 'none' }} variant="contained" color="success" onClick={handleSave} disabled={isValid !== true}>Save</Button>}
                    <Button style={{ margin: '10px', float: 'right', width: '100px', backgroundColor: 'white', color: '#FD5107', borderColor: '#FD5107', borderStyle: 'solid', borderWidth: '10', textTransform: 'none' }} variant="outlined" color="warning" onClick={handleClose}>Cancel</Button>
            </DialogContent>
        </Dialog>
    );
};

export default UserRoleDetails;